// cp-api client (docs/2026-10-08-rfc-cp-api.md, cp-api/openapi.yaml). Every request carries the session cookie
// (`credentials: 'include'`): same-origin through the Vite proxy locally, CORS with credentials on stage.

export class ApiError extends Error {
  constructor(status, code, message, retryAfter, details = {}) {
    super(message);
    this.status = status;
    this.code = code;
    this.retryAfter = retryAfter;
    /** The rest of the error object (e.g. `scarfall_code` and `publication` on `publish_refused`). */
    this.details = details;
  }
}

export class Api {
  /** `onUnauthenticated` runs on any 401 outside login (the session cookie expired or is missing). */
  constructor(base, { onUnauthenticated } = {}) {
    this.base = base;
    this.onUnauthenticated = onUnauthenticated;
  }

  async request(method, path, { body, headers = {}, auth = true } = {}) {
    let res;
    try {
      res = await fetch(`${this.base}${path}`, {
        method,
        credentials: 'include',
        headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}), ...headers },
        body: body ? JSON.stringify(body) : undefined,
      });
    } catch (err) {
      throw new ApiError(0, 'network', `cp-api unreachable: ${err.message ?? err}`);
    }
    const text = await res.text();
    let data;
    try {
      data = text ? JSON.parse(text) : null;
    } catch {
      data = null;
    }
    if (!res.ok) {
      const error = data?.error ?? {};
      const err = new ApiError(res.status, error.code ?? 'http_error', error.message ?? `HTTP ${res.status}`,
        Number(res.headers.get('Retry-After')) || undefined, error);
      if (res.status === 401 && auth) this.onUnauthenticated?.(err);
      throw err;
    }
    return data;
  }

  login(username, password) {
    return this.request('POST', '/v1/auth/login', { body: { username, password }, auth: false });
  }

  logout() {
    return this.request('POST', '/v1/auth/logout', { auth: false });
  }

  assets() {
    return this.request('GET', '/v1/assets');
  }

  creations({ state, limit = 20, cursor } = {}) {
    const q = new URLSearchParams({ limit: String(limit) });
    if (state) q.set('state', state);
    if (cursor) q.set('cursor', cursor);
    return this.request('GET', `/v1/creations?${q}`);
  }

  createCreation(assetId, title) {
    return this.request('POST', '/v1/creations', { body: { asset_id: assetId, ...(title ? { title } : {}) } });
  }

  creation(id, { turnsBefore, limit } = {}) {
    const q = new URLSearchParams();
    if (turnsBefore) q.set('turns_before', String(turnsBefore));
    if (limit) q.set('limit', String(limit));
    return this.request('GET', `/v1/creations/${id}${q.size ? `?${q}` : ''}`);
  }

  assetFiles(creationId) {
    return this.request('GET', `/v1/creations/${creationId}/asset`);
  }

  version(creationId, version) {
    return this.request('GET', `/v1/creations/${creationId}/versions/${version}`);
  }

  /** Repeating `idempotencyKey` returns the original turn, so a retried submit never runs twice. */
  submitTurn(creationId, prompt, idempotencyKey) {
    return this.request('POST', `/v1/creations/${creationId}/turns`, {
      body: { prompt },
      headers: { 'Idempotency-Key': idempotencyKey },
    });
  }

  turn(id) {
    return this.request('GET', `/v1/turns/${id}`);
  }

  cancelTurn(id) {
    return this.request('DELETE', `/v1/turns/${id}`);
  }

  eventsUrl(turnId) {
    return `${this.base}/v1/turns/${turnId}/events`;
  }

  // Publishing to Scarfall (docs/2026-10-16-rfc-ugc-publish.md).

  /** `{version, unique_id, title, rarity}`; repeating it for the same version and player returns the open one. */
  publish(creationId, body) {
    return this.request('POST', `/v1/creations/${creationId}/publications`, { body });
  }

  publications(creationId) {
    return this.request('GET', `/v1/creations/${creationId}/publications`);
  }

  publicationEventsUrl(creationId) {
    return `${this.base}/v1/creations/${creationId}/publications/events`;
  }
}
